import { useCallback, useEffect, useRef, useState } from "react";
import { uploadAsset } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { MP4_PREFIX_BYTES, inspectMp4 } from "../../lib/mp4-inspect";
import {
  LANDING_UPLOAD_ACCEPT, LANDING_UPLOAD_MODULE, UPLOAD_ACCEPT, UPLOAD_NAMED_ACCEPT, landingUploadRejectReason, VIDEO_UPLOAD_ACCEPT, VIDEO_UPLOAD_MODULE, namedFileRejectReason, uploadRejectReason,
  videoUploadRejectReason,
} from "../../lib/upload";

/** 上传完成时随回调带出的附加信息。目前只有视频位有：服务端从 mp4 文件头读出的时长 */
export interface UploadMeta { durationSec?: number }

type OnDone<T> = (url: string, target: T, name: string, file: File, meta: UploadMeta) => void | Promise<void>;

/**
 * 上传素材的通用件——**接新模块时只用这个，不要自己写 file input。**
 *
 * 封装掉的东西（leo 2026-08-26：别的同事上传不用管配置）：
 *   · 受理哪些格式、多大——来自 lib/upload.ts，与服务端同一份，页面不用知道
 *   · 挑完先自查再传——省掉一趟必然失败的上传（超大文件尤其明显）
 *   · 隐藏的 <input type="file">、传完清空 value（否则同一个文件选第二次不触发）
 *   · busy / 错误文案
 *   · **目标锁定**：点「上传」时就把这次的去向固定住，上传期间用户再点另一个框，
 *     结果不会改道。AssetDialog 一个对话框里有图片和动画两个格子，早期就踩过。
 *
 * 桶、ptag、CDN 域名都在服务端决定（src/media.ts 按环境查表），管端一个字都不用配。
 *
 * 用法（module = 这个模块在 CDN 上的目录名 `aeroplane/<module>/…`，各模块用自己的，别混）：
 * ```tsx
 * const up = useAssetUpload<"img" | "anim">({ module: "cell" });
 * <Button disabled={up.busy} onClick={() => up.pick("img", async (url) => { await save(url); })}>上传</Button>
 * {up.busy && <span>上传中…</span>}
 * {up.error && <Notice tone="danger">{up.error}</Notice>}
 * {up.input}
 * ```
 */
export function useAssetUpload<T = void>(opts: {
  module?: string;
  /**
   * Spine 这类**按文件名后缀受理**的素材（`.skel` / `.atlas` / `.meta` 没有 MIME，
   * 上面那张 MIME 表一条都用不上）。开了它：
   *   · accept 换成后缀清单，`<input>` 变多选
   *   · 校验走 namedFileRejectReason
   *   · 上传时把原文件名一并带给服务端（对象键里保留原名，且落库存 {name, url} 对）
   *
   * **不判数量、不判 6 个后缀齐不齐** —— 那是页面的事（lib/pet.ts 的 spineGroupIssue）。
   */
  byName?: boolean;
  /**
   * VIP 视频位（只收 mp4）。开了它：
   *   · module 固定为 idol-vip-video（服务端只在这个上传位收视频），accept 换成 video/mp4
   *   · 传之前先读文件开头一段做 mp4 体检（lib/mp4-inspect.ts，与服务端同一份）：
   *     索引不在开头、H.265、分片之类的文件在本地就拦下，不白传 50MB
   *   · 回调第五个参数带服务端读出的时长 `meta.durationSec`
   */
  video?: boolean;
} = {}): {
  /**
   * 打开文件选择器；选完自动校验 → 上传 → 回调拿到完整 CDN URL。
   *
   * `byName` 下可多选：**逐个串行上传，每传完一个回调一次**。串行不是图省事——
   * 并发的话六个回调会各自基于同一份旧 state 算新值，最后一个覆盖前五个，
   * 于是六个文件只剩一个进了表单，而且不报错。
   *
   * 回调第四个参数是原文件：要在浏览器里量尺寸做软提示的页面用（lib/media-hints.ts），其余页面不用管。
   */
  pick: (target: T, onDone: OnDone<T>) => void;
  /** 上传中或回调执行中 */
  busy: boolean;
  /** 校验失败、上传失败、或回调抛出的错误文案；null = 没有错误 */
  error: string | null;
  /** 手动清错误（比如用户改了别的东西） */
  clearError: () => void;
  /** 把它渲染到组件树里任意位置——隐藏元素，不影响布局 */
  input: React.ReactElement;
  /**
   * 给「清空 / 删除」这类不涉及文件、但同样要 busy + 错误处理的动作用，
   * 省得页面为它再维护一套 busy/err。
   */
  run: (fn: () => void | Promise<void>) => Promise<void>;
} {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  // 用 ref 不用 state：点击到 onChange 之间不重渲染，state 会慢一拍
  const pending = useRef<{ target: T; onDone: OnDone<T> } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; pending.current = null; };
  }, []);

  const run = useCallback(async (fn: () => void | Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      if (active.current) setError((e as Error).message);
    } finally {
      if (active.current) setBusy(false);
    }
  }, []);

  const pick = useCallback((target: T, onDone: OnDone<T>) => {
    setError(null);
    pending.current = { target, onDone };
    fileRef.current?.click();
  }, []);

  const onChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = [...(e.target.files ?? [])];
      const job = pending.current;
      pending.current = null;
      // 传完立刻清空：不清的话同一个文件选第二次不会触发 change
      if (fileRef.current) fileRef.current.value = "";
      if (!files.length || !job) return;

      /*
       * 全组先查一遍再传：**校验**是全有或全无，一个不合格就一个都不发。
       *
       * ⚠️ **上传本身不是原子的**：第 4 个失败时，前 3 个已经进了 CDN 也已经回调过，
       * 后 2 个不会发。这是刻意接受的——重新挑齐 6 个即可，回调按文件名替换、
       * 表单里不会出现重复项，代价只是 CDN 上多躺 3 个没人引用的对象。
       * 而那正是本仓库既有的策略（改素材 = 换 URL，永不覆盖，见 src/admin/assets.ts），
       * 为它做两阶段提交要多一套「已传但未确认」的状态，不值当。
       */
      for (const f of files) {
        const reject = opts.video ? videoUploadRejectReason(f.type, f.size)
          : opts.byName ? namedFileRejectReason(f.name, f.size)
          : opts.module === LANDING_UPLOAD_MODULE ? landingUploadRejectReason(f.type, f.size) : uploadRejectReason(f.type, f.size);
        if (reject) {
          // 浏览器认不出扩展名时 file.type 是空串，直接插值会得到「选中的是 」
          const vars = reject.vars?.type === "" ? { ...reject.vars, type: t("upload.unknownType") } : reject.vars;
          setError(t(reject.key, vars));
          return;
        }
        if (opts.video) {
          // 只读开头一段：faststart 的 moov 一般几百 KB。服务端拿整个文件还会再判一次，这里只为少白传一趟
          const head = new Uint8Array(await f.slice(0, MP4_PREFIX_BYTES).arrayBuffer());
          const r = inspectMp4(head, f.size <= MP4_PREFIX_BYTES);
          if (!active.current) return;
          if (!r.ok) { setError(t(`upload.mp4.${r.reason}`)); return; }
        }
      }
      await run(async () => {
        /*
         * 串行，不 Promise.all：并发时六个回调各自基于同一份旧 state 算新值，
         * 最后一个覆盖前五个——六个文件只剩一个进表单，且不报错。
         *
         * active.current 每一轮都要查（test 那侧加的）：编辑器可能在上传途中被关掉、
         * 或为另一个租户重挂，而回调会落库——**多文件下这一点更要紧**，
         * 因为一次 pick 会跨越好几次网络往返，中途卸载的窗口比单文件大得多。
         * 已经传上 CDN 的那几个就留在那儿（改素材 = 换 URL，本来就不覆盖）。
         */
        for (const file of files) {
          const res = await uploadAsset(file, opts.video ? VIDEO_UPLOAD_MODULE : opts.module, opts.byName ? file.name : undefined);
          if (!active.current) return;
          await job.onDone(res.url, job.target, file.name, file, { durationSec: res.duration_sec });
        }
      });
    },
    [run, opts.module, opts.byName, opts.video, t],
  );

  const input = (
    <input
      ref={fileRef} type="file" className="hidden"
      accept={opts.video ? VIDEO_UPLOAD_ACCEPT : opts.byName ? UPLOAD_NAMED_ACCEPT : opts.module === LANDING_UPLOAD_MODULE ? LANDING_UPLOAD_ACCEPT : UPLOAD_ACCEPT}
      multiple={opts.byName}
      onChange={(e) => void onChange(e)}
    />
  );

  return { pick, busy, error, clearError: () => setError(null), input, run };
}
